import React, {useState, useEffect, useCallback} from "react";
import {Modal, Button, Tooltip} from "antd";
import moment from "moment";
import {useObserver} from "mobx-react-lite";
import {useStore} from "hooks/useStore";
import {ResponsiveDataTableStyled} from "components/_defaultComponents/ResponsiveDataTable";

const RETURNABLE_STATUS = "handovered";

const equipmentStatusLabels = {
    returned: "İade Alındı",
    handovered: "Teslim Verildi",
    pendingAssignment: "Atama Beklemede"
};

const formatDate = date => (date ? moment(date).format("DD.MM.YYYY") : "-");

const columns = [
    {
        title: "Ekipman Adı",
        dataIndex: "equipmentName",
        key: "equipmentName",
        render: value => value || "-",
        onCell: () => ({"data-label": "Ekipman Adı"})
    },
    {
        title: "Ekipman Türü",
        dataIndex: "equipmentType",
        key: "equipmentType",
        render: value => value || "-",
        onCell: () => ({"data-label": "Ekipman Türü"})
    },
    {
        title: "Renk",
        dataIndex: "equipmentColor",
        key: "equipmentColor",
        render: value => value || "-",
        onCell: () => ({"data-label": "Renk"})
    },
    {
        title: "Beden",
        dataIndex: "equipmentSize",
        key: "equipmentSize",
        render: value => value || "-",
        onCell: () => ({"data-label": "Beden"})
    },
    {
        title: "Fiyat",
        dataIndex: "price",
        key: "price",
        render: price => (price ? `${price} ₺` : "-"),
        onCell: () => ({"data-label": "Fiyat"})
    },
    {
        title: "Teslim Tarihi",
        dataIndex: "handoverDate",
        key: "handoverDate",
        render: formatDate,
        onCell: () => ({"data-label": "Teslim Tarihi"})
    },
    {
        title: "İade Tarihi",
        dataIndex: "returnDate",
        key: "returnDate",
        render: formatDate,
        onCell: () => ({"data-label": "İade Tarihi"})
    },
    {
        title: "Durum",
        dataIndex: "status",
        key: "status",
        render: status => equipmentStatusLabels[status] || status || "-",
        onCell: () => ({"data-label": "Durum"})
    }
];

const ReturnEquipmentModal = ({visible, courier, onCancel, onSuccess = () => {}}) => {
    const stores = useStore();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [equipments, setEquipments] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);

    const courierId = courier?._id;

    const fetchEquipments = useCallback(async () => {
        if (!courierId) return;
        setLoading(true);
        try {
            const response = await stores.equipmentStore.courierEquipmentDetail({id: courierId});
            setEquipments(response?.result || []);
        } finally {
            setLoading(false);
        }
    }, [courierId, stores.equipmentStore]);

    useEffect(() => {
        if (!visible) return;
        setSelectedIds([]);
        setCurrentPage(1);
        fetchEquipments();
    }, [visible, fetchEquipments]);

    const handleReturn = () => {
        Modal.confirm({
            title: "İade onayı",
            content: "Seçilen ekipmanlar kuryeden iade alınacaktır, onaylıyor musun?",
            okText: "Onaylıyorum",
            cancelText: "İptal Et",
            onOk: async () => {
                setSubmitting(true);
                try {
                    const result = await stores.equipmentStore.returnEquipmentFromCourier({
                        ids: selectedIds
                    });
                    if (result) {
                        setSelectedIds([]);
                        onSuccess();
                        onCancel();
                    }
                } finally {
                    setSubmitting(false);
                }
            }
        });
    };

    return useObserver(() => (
        <Modal
            title="Ekipman İade Al"
            visible={visible}
            onCancel={onCancel}
            width={1000}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    İptal Et
                </Button>,
                <Button
                    key="return"
                    type="primary"
                    loading={submitting}
                    disabled={selectedIds.length === 0}
                    onClick={handleReturn}
                >
                    İade Al
                </Button>
            ]}
        >
            <p>Kuryeye teslim edilen ekipmanlardan iade alınacak olanları seçin.</p>
            <ResponsiveDataTableStyled
                loading={loading}
                dataSource={equipments}
                columns={columns}
                rowKey="_id"
                size="small"
                pagination={{
                    current: currentPage,
                    pageSize: 10,
                    showSizeChanger: false,
                    onChange: setCurrentPage
                }}
                scroll={{x: "max-content"}}
                locale={{emptyText: "Kuryeye ait ekipman bulunamadı"}}
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: setSelectedIds,
                    getCheckboxProps: record => ({
                        disabled: record?.status !== RETURNABLE_STATUS
                    }),
                    renderCell: (checked, record, index, originNode) =>
                        record?.status !== RETURNABLE_STATUS ? (
                            <Tooltip title="Ekipman teslim edilmişse seçilebilir">
                                {originNode}
                            </Tooltip>
                        ) : (
                            originNode
                        )
                }}
            />
        </Modal>
    ));
};

export default ReturnEquipmentModal;
